// The new admin home's API (worker routes/dashboard.ts, 2026-10-21). A file of
// its own so parallel work on lib/api.ts never collides (same as pushPrefsApi).
import { apiFetch } from './api';

export type TodoKind = 'validate' | 'reply' | 'preview' | 'ship';

export type TodoDetail =
  | { kind: 'photos'; n: number }
  | { kind: 'changes' }
  | { kind: 'said'; text: string }
  | null;

export interface TodoItem {
  key: string;
  kind: TodoKind;
  verb: string;
  button: string;
  customer_name: string | null;
  order_ref: string | null;
  order_id: string | null;
  shopify_order_id: string | null;
  chat_id: string | null;
  since: string;
  amount: number | null;
  assigned_agent_id: string | null;
  takeover_owner: string | null;
  mine: boolean;
  old: boolean;
  detail: TodoDetail;
  href: string;
}

export interface TodoResponse {
  now: string;
  items: TodoItem[];
  counts: { total: number; mine: number; old: number; oldMine: number };
}

export interface ProfitSummary {
  window: { from: string | null; to: string | null };
  orders: number;
  revenue: number;
  productionCost: number;
  deliveryCost: number;
  albumCost: number;
  deliveryEstimatedCount: number;
  missingCostCount: number;
  costIncomplete: boolean;
  commissions: number;
  ads: { total: number; missing: boolean; lastSpendDate: string | null; reason: string | null };
  expenses: { total: number; count: number; oneTime: number; recurring: number; variable: number };
  /** AI costs (2026-10-22, worker lib/aiCostLedger.ts). Optional: an older worker has none.
   *  total = réel (full value); paid = payé (OpenRouter + Claude not covered by its credit, 2026-10-23 —
   *  optional: an older worker has none, read it as = total); basis = the one subtracted. */
  ai?: { total: number; paid?: number; basis?: AiBasis; missing: boolean; reason: string | null; trackingSince: string | null; partial: boolean; calls: number };
  /** Which AI figure profitNet subtracts (default 'real'). Optional: older worker. */
  aiBasis?: AiBasis;
  /** The profit with each AI figure — the SAME formula. Optional: older worker. */
  byAiBasis?: Record<AiBasis, ProfitByAi>;
  profitBeforeAds: number;
  profitNet: number | null;
  /** revenue minus every KNOWN cost; equals profitNet when nothing is missing. */
  profitKnown?: number;
  missingParts?: Array<'ads' | 'ai'>;
  components?: ProfitComponents;
  margin: number | null;
  perOrder: { revenue: number; profit: number | null } | null;
}

/** Coût IA réel (full value, even what Claude's free credit covers) or payé (cash actually spent). */
export type AiBasis = 'real' | 'paid';
export interface ProfitByAi { aiMad: number; profitBeforeAds: number; profitNet: number | null; profitKnown: number; margin: number | null; perOrderProfit: number | null }

/** One cost line of the « Calculateur de profit ». */
export interface ProfitComponent { amount: number; missing: boolean; incomplete: boolean; reason: string | null }
export type ProfitComponentKey = 'production' | 'delivery' | 'commissions' | 'ads' | 'ai' | 'expenses';
export type ProfitComponents = Record<ProfitComponentKey, ProfitComponent>;

export interface ProfitResponse { generatedAt: string; period: { from: string | null; to: string | null }; profit: ProfitSummary }

/** mad/usd = réel; paidMad/paidUsd = payé (optional: a worker before 2026-10-23 has none → = réel). */
export interface AiCostRow { name: string; calls: number; mad: number; usd: number; paidMad?: number; paidUsd?: number }
export interface AiWindowTotal { mad: number; calls: number; missing: boolean; paidMad?: number }
/** Claude's share of a period: réel value, the part its free credit covered, the part paid. */
export interface AiClaudeShare { calls: number; usd: number; mad: number; coveredUsd: number; coveredMad: number; paidUsd: number; paidMad: number }
/** The latest daily Claude check (ai_claude_checks): our total vs Anthropic's. */
export interface AiClaudeCheck { day: string; source: string; periodFrom: string | null; anthropicUsd: number; oursUsd: number; gapPct: number | null; flagged: boolean }
export interface AiGapDay { day: string; key: 'main' | 'finder'; openrouterUsd: number; ledgerUsd: number; gapPct: number | null; flagged: boolean; source: string }
export interface AiCostsResponse {
  generatedAt: string;
  fxUsd: number;
  trackingSince: string | null;
  completeSince: string | null;
  installed: boolean;
  totals: { today: AiWindowTotal; d7: AiWindowTotal; month: AiWindowTotal };
  period: {
    from: string; to: string | null;
    mad: number; usd: number; calls: number; pendingCost: number;
    /** Payé (2026-10-23) — optional: older worker. */
    paidMad?: number; paidUsd?: number;
    claude?: AiClaudeShare;
    missing: boolean; reason: string | null; partial: boolean;
    byFeature: AiCostRow[]; byModel: AiCostRow[];
    /** OpenRouter / Claude (name 'openrouter' | 'anthropic'). Optional: older worker. */
    byProvider?: AiCostRow[];
    byDay: Array<{ day: string; mad: number | null; paidMad?: number | null }>;
    perDeliveredOrder: { orders: number; withAi: number; mad: number; avg: number | null } | null;
    topOrders: Array<{ orderId: string; shopifyOrderId: string | null; name: string | null; customer: string | null; calls: number; mad: number; paidMad?: number; claudeMad?: number }>;
    topOrdersClaudeCapped?: boolean;
  };
  gap: { days: AiGapDay[]; flagged: { day: string; key: 'main' | 'finder'; gapPct: number } | null } | null;
  /** The latest Claude check; null = none yet. Optional: older worker. */
  claudeCheck?: AiClaudeCheck | null;
  /** The rollup came from ai_cost_rollup_v2 (migration 264). */
  rollupV2?: boolean;
}

export interface OrderAiCost { orderId: string; missing: boolean; mad: number | null; usd?: number; calls: number; fxUsd: number }

export interface PipelineStep {
  key: 'photos' | 'album' | 'validated' | 'printing' | 'shipped' | 'delivered';
  label: string;
  sub: string;
  statuses: string[];
  count: number;
  over48h: number;
  old: number;
}

// ─── v2 cockpit (2026-10-22, worker lib/homeVerdict.ts + lib/homeKpis.ts) ────

export type VerdictTone = 'red' | 'amber' | 'green';
export type VerdictKind =
  | 'waiting' | 'loss' | 'adsStale' | 'aiSpike'
  | 'ordersDrop' | 'stuckParcels' | 'albumsLate' | 'aiGap'
  | 'profitable' | 'nothingWaiting' | 'ordersUp' | 'ordersStable' | 'productionOnTime' | 'noStuckParcels' | 'allGood';

export interface VerdictTile {
  kind: VerdictKind;
  tone: VerdictTone;
  title: string;
  detail: string;
  cta: 'Traiter' | 'Voir' | 'Détail';
  /** A route, or « #home-todo » / « #home-pipeline ». */
  href: string;
  params: Record<string, string | number>;
}

export interface DeliveryStats { delivered: number; returned: number; avgDays: number | null; successRate: number | null }

export interface KpiNumbers {
  revenue: number;
  delivered: number;
  profitNet: number | null;
  profitBeforeAds: number;
  margin: number | null;
  newOrders: number;
  avgBasket: number | null;
  avgProfit: number | null;
  adSpend: number | null;
  adPerOrder: number | null;
  roas: number | null;
  /** Coût IA réel (DH) — optional: an older worker has none. Null = données manquantes. */
  aiCost?: number | null;
  /** Coût IA payé (DH): OpenRouter + Claude not covered by its credit (2026-10-23). Optional: older worker. */
  aiPaid?: number | null;
  /** Profit net « avec coût IA payé » (profitNet = « avec coût IA réel », the default). Optional: older worker. */
  profitNetPaid?: number | null;
  aiPerOrder?: number | null;
  aiPartial?: boolean;
  missingParts?: Array<'ads' | 'ai'>;
  profitKnown?: number;
  delivery: DeliveryStats;
  /** « Pub par lead / commande / confirmée / livrée » — optional: an older worker has none. */
  adBasis?: Record<AdBasis, { count: number; perUnit: number | null }>;
  /** Taux de confirmation by lead source (cohort: leads created in the period). Optional: older worker. */
  confirmation?: { site: RateCell; whatsapp: RateCell; all: RateCell };
}

export interface RateCell { leads: number; confirmed: number; rate: number | null }

/** What one unit costs in ads (worker lib/homeKpis.ts AD_BASES). */
export type AdBasis = 'lead' | 'order' | 'confirmed' | 'delivered';

export interface SeriesPoint {
  day: string;
  revenue: number;
  profitNet: number | null;
  newOrders: number;
  delivered: number;
  avgBasket: number | null;
  adSpend: number | null;
  adPerOrder: number | null;
  aiCost?: number | null;
  deliveryDays: number | null;
  /** That day's leads confirmation rate (0–1), null when no lead. */
  confirmRate?: number | null;
}

export interface OverviewResponse {
  /** v2 fields — optional so an older worker still renders the rest. */
  generatedAt?: string;
  verdict?: VerdictTile[];
  kpis?: { current: KpiNumbers; previous: KpiNumbers | null };
  series?: SeriesPoint[];
  stuckParcels?: { count: number };
  period: { from: string | null; to: string | null };
  previousPeriod: { from: string; to: string } | null;
  money: {
    current: ProfitSummary;
    previous: ProfitSummary | null;
    today: { day: string; cash: number; deliveredCount: number; advances: number; advancesPeriod: number };
    toCollect: {
      amount: number; orders: number;
      pendingPhotosAmount: number; pendingPhotosOrders: number;
      oldExcluded: number; oldExcludedAmount: number;
    };
  };
  pipeline: { steps: PipelineStep[] };
}

export interface StaleOrder {
  shopify_order_id: string;
  order_ref: string;
  customer_name: string | null;
  status: string;
  step: PipelineStep['key'];
  last_activity_at: string;
  days: number;
  value: number;
  assigned_agent_id: string | null;
}

export interface StaleResponse { stale: StaleOrder[]; total: number; value: number }

const qs = (p: { from?: string; to?: string; preset?: string }) => {
  const q = new URLSearchParams();
  if (p.from) q.set('from', p.from);
  if (p.to) q.set('to', p.to);
  if (p.preset) q.set('preset', p.preset);
  const s = q.toString();
  return s ? `?${s}` : '';
};

/** A task the loop alarm stopped (worker lib/ai/pauses.ts, 2026-11-02). */
export interface AiPauseRow {
  id: string; feature: string; order_id: string | null; chat_id: string | null;
  reason: string; calls: number | null; failed: number | null; paused_at: string; until: string;
  orderName: string | null; shopifyOrderId: string | null;
}

export const dashboardApi = {
  todo: () => apiFetch<TodoResponse>('/dashboard/todo'),
  overview: (range: { from?: string; to?: string; preset?: string }) => apiFetch<OverviewResponse>(`/dashboard/overview${qs(range)}`),
  stale: () => apiFetch<StaleResponse>('/dashboard/stale'),
  profit: (range: { from?: string; to?: string }) => apiFetch<ProfitResponse>(`/dashboard/profit${qs(range)}`),
  aiCosts: (range: { from?: string; to?: string }) => apiFetch<AiCostsResponse>(`/dashboard/ai-costs${qs(range)}`),
  aiPauses: () => apiFetch<{ installed: boolean; pauses: AiPauseRow[] }>('/dashboard/ai-pauses'),
  resumeAiPause: (id: string) => apiFetch<{ ok: true }>(`/dashboard/ai-pauses/${id}/resume`, { method: 'POST' }),
  aiCalls: (f: { from?: string; to?: string; feature?: string; order?: string; errors?: boolean; before?: string }) => {
    const q = new URLSearchParams();
    if (f.from) q.set('from', f.from);
    if (f.to) q.set('to', f.to);
    if (f.feature) q.set('feature', f.feature);
    if (f.order) q.set('order', f.order);
    if (f.errors) q.set('errors', '1');
    if (f.before) q.set('before', f.before);
    const s = q.toString();
    return apiFetch<AiCallsResponse>(`/dashboard/ai-calls${s ? `?${s}` : ''}`);
  },
  orderAiCost: (orderId: string) => apiFetch<OrderAiCost>(`/dashboard/ai-costs/order/${encodeURIComponent(orderId)}`),
};

/** One AI call (worker GET /dashboard/ai-calls). */
export interface AiCallRow {
  id: string; at: string; feature: string; kind: string | null; model: string; key: string;
  order: { id: string; shopifyOrderId: string | null; name: string | null; customer: string | null } | null;
  chatId: string | null; chatLabel: string | null;
  tokensIn: number | null; tokensOut: number | null;
  usd: number | null; mad: number | null; costSource: string;
  ok: boolean; error: string | null; ms: number | null;
}
export interface AiCallsResponse { rows: AiCallRow[]; next: string | null; fxUsd: number; orderNotFound?: boolean; installed?: boolean }
